// src/pages/admin/CourseLectures/lectures/Lecture06ErfolgreichBewerben.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Process Step Card ─────────────────────────────────────────────
const StepCard: React.FC<{
  step: string;
  icon: string;
  label: string;
  desc: string;
  tip?: string | null;
  cls: string;
  tipColor?: string;
}> = ({ step, icon, label, desc, tip, cls, tipColor = "text-gray-500" }) => (
  <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${cls}`}>
    <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-sm font-extrabold flex-shrink-0">
      {step}
    </div>
    <div className="flex-1">
      <p className="text-sm font-bold">
        {icon} {label}
      </p>
      <p className="text-xs opacity-80 mt-0.5">{desc}</p>
      {tip && <p className={`text-[11px] italic mt-1 ${tipColor}`}>💡 {tip}</p>}
    </div>
  </div>
);

// ── Email Example Block ───────────────────────────────────────────
const EmailExample: React.FC = () => (
  <div className="bg-gray-900 rounded-xl p-4 text-xs font-mono">
    <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-3">
      Beispiel E-Mail:
    </p>
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <span className="text-gray-500 flex-shrink-0 w-16">Betreff:</span>
        <span className="text-amber-300">
          Bewerbung als [Beruf] – Referenznummer: 12345 – [Vorname Nachname]
        </span>
      </div>
      <div className="border-t border-gray-700 my-2" />
      <p className="text-gray-300">Sehr geehrte Frau Müller,</p>
      <p className="text-gray-300 mt-1">
        hiermit bewerbe ich mich mit großem Interesse um den Ausbildungsplatz
        als [Beruf] in Ihrem Unternehmen.
      </p>
      <p className="text-gray-300 mt-1">
        Warum ich mich genau bei Ihnen bewerbe und welche Stärken ich mitbringe,
        entnehmen Sie bitte meinem ausführlichen Anschreiben im Anhang. Dort
        finden Sie auch meinen Lebenslauf und meine Zeugnisse.
      </p>
      <p className="text-gray-300 mt-1">
        Für ein Online Gespräch stehe ich Ihnen gerne zur Verfügung, da ich
        zurzeit in Bangladesch wohne.
      </p>
      <div className="mt-2 space-y-0.5 text-gray-300">
        <p>Mit freundlichen Grüßen</p>
        <p className="text-[#009fe3]">Vorname Nachname</p>
        <p>Tel: +8801234567890</p>
      </div>
      <div className="border-t border-gray-700 mt-2 pt-2">
        <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-1">
          Anlagen:
        </p>
        <p className="text-green-400">
          Bewerbung_Ausbildung_Beruf_Vorname_Nachname.pdf
        </p>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture06ErfolgreichBewerben: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 05 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "3 Lebensläufe und Anschreiben vorbereitet",
            "Ordnerstruktur erstellt (JJJJ-MM-TT_Firma_Beruf)",
            "Fokus auf B2-Sprachniveau & Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Online-Portale — Wie sie funktionieren" },
          { num: "2", label: "Online-Portale — Der Bewerbungsprozess" },
          { num: "3", label: "E-Mail-Bewerbung — Empfänger & Betreffzeile" },
          { num: "4", label: "E-Mail-Bewerbung — Text & Anhang" },
          { num: "5", label: "E-Mail-Bewerbung — Beispiel" },
        ]}
      />

      {/* 01 — Online-Portale Überblick */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="🌐"
        title="Online-Portale — Wie sie funktionieren"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Viele Unternehmen nutzen eigene Karriereseiten, auf denen man ein
          Profil anlegen muss.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🏢</span>
              <span className="text-sm font-bold text-blue-800">
                Eigene Karriereseiten
              </span>
            </div>
            <p className="text-xs text-blue-700">
              Große Unternehmen betreiben eigene Bewerbungsportale. Dort musst
              du ein persönliches Konto erstellen, bevor du dich bewerben
              kannst.
            </p>
          </div>
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">⚠️</span>
              <span className="text-sm font-bold text-orange-800">
                Worauf du achten musst
              </span>
            </div>
            <ul className="space-y-1 text-xs text-orange-700">
              <li className="flex gap-1.5">
                <span className="flex-shrink-0">›</span>Professionelle
                E-Mail-Adresse verwenden
              </li>
              <li className="flex gap-1.5">
                <span className="flex-shrink-0">›</span>Sicheres Passwort wählen
                und notieren
              </li>
              <li className="flex gap-1.5">
                <span className="flex-shrink-0">›</span>Zugangsdaten gut
                aufbewahren
              </li>
            </ul>
          </div>
        </div>
        <InfoBox variant="info" icon="ℹ️" title="Tipp zur E-Mail-Adresse">
          Nutze eine professionelle Adresse im Format{" "}
          <code className="bg-white px-1 rounded text-xs border border-gray-200">
            vorname.nachname@gmail.com
          </code>{" "}
          — keine Spitznamen oder Zahlen wie "coolguy99".
        </InfoBox>
      </Accordion>

      {/* 02 — Online-Portale Prozess */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="📋"
        title="Online-Portale — Der Bewerbungsprozess"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          So läuft eine typische Online-Portal-Bewerbung Schritt für Schritt ab:
        </p>
        <div className="space-y-2 mb-4">
          {(
            [
              {
                step: "1",
                icon: "👤",
                label: "Account erstellen",
                desc: "Professionelle E-Mail-Adresse und sicheres Passwort nutzen. Zugangsdaten notieren.",
                tip: "Erstelle für jede Bewerbungsrunde eine einheitliche E-Mail — Personalchefs googeln dich.",
                cls: "bg-blue-50 border-blue-200 text-blue-800",
                tipColor: "text-blue-600",
              },
              {
                step: "2",
                icon: "✏️",
                label: "Daten eingeben",
                desc: "Oft müssen Lebenslauf-Daten (Schule, Praktika, Qualifikationen) manuell in Felder eingetragen werden.",
                tip: "Halte deinen Lebenslauf als Referenz griffbereit — das spart Zeit.",
                cls: "bg-purple-50 border-purple-200 text-purple-800",
                tipColor: "text-purple-600",
              },
              {
                step: "3",
                icon: "📄",
                label: "Dokumente hochladen",
                desc: "Lebenslauf, Zeugnisse und Anschreiben als PDF hochladen.",
                tip: null,
                cls: "bg-green-50 border-green-200 text-green-800",
                tipColor: "text-green-600",
              },
              {
                step: "4",
                icon: "📦",
                label: "PDF zusammenführen",
                desc: "Wenn ein einzelnes PDF hochgeladen werden kann: Anschreiben + Lebenslauf + Zeugnisse in einem PDF zusammenführen.",
                tip: null,
                cls: "bg-amber-50 border-amber-200 text-amber-800",
                tipColor: "text-amber-600",
              },
            ] as {
              step: string;
              icon: string;
              label: string;
              desc: string;
              tip: string | null;
              cls: string;
              tipColor: string;
            }[]
          ).map((item) => (
            <StepCard key={item.step} {...item} />
          ))}
        </div>

        {/* Filename convention */}
        <div className="bg-gray-900 rounded-xl px-4 py-3 text-xs">
          <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-1.5">
            Professionelle Dateibezeichnung:
          </p>
          <code className="text-green-400 font-mono">
            Bewerbung_Ausbildung_Beruf_Vorname_Nachname.pdf
          </code>
          <p className="text-gray-500 mt-1.5 text-[10px]">
            → Kein "Bewerbung_final2.pdf" oder "dokument(3).pdf"
          </p>
        </div>

        <div className="mt-3">
          <InfoBox variant="tip" icon="🔗" title="PDF zusammenführen">
            Nutze{" "}
            <a
              href="https://www.ilovepdf.com/merge_pdf"
              target="_blank"
              rel="noreferrer"
              className="underline font-semibold"
            >
              ilovepdf.com/merge_pdf
            </a>{" "}
            um mehrere PDFs kostenlos zu einem zusammenzuführen.
          </InfoBox>
        </div>
      </Accordion>

      {/* 03 — E-Mail Empfänger & Betreff */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="✉️"
        title="E-Mail-Bewerbung — Empfänger & Betreffzeile"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Die E-Mail-Bewerbung ist der Standard bei kleinen und
          mittelständischen Unternehmen — und oft der direkteste Weg zum
          Recruiter.
        </p>
        <div className="space-y-3 mb-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-bold text-blue-800 mb-2">
              👤 Empfänger — Wen ansprechen?
            </p>
            <div className="space-y-1.5 text-sm text-blue-700">
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5 font-bold text-green-600">
                  ✓
                </span>
                <span>
                  Versuche immer, einen{" "}
                  <strong>konkreten Ansprechpartner</strong> herauszufinden
                  (Website checken oder anrufen)
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5 font-bold text-red-500">
                  ✗
                </span>
                <span>
                  Vermeide "Sehr geehrte Damen und Herren" — wenn irgend möglich
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-bold text-green-800 mb-2">
              📌 Betreffzeile — Das Erste, was der Recruiter sieht
            </p>
            <p className="text-xs text-green-700 mb-2">
              Die Betreffzeile muss sofort klar machen, worum es geht.
            </p>
            <div className="bg-white rounded-lg px-3 py-2 border border-green-200">
              <p className="text-[10px] text-green-600 uppercase tracking-wide mb-1">
                Muster:
              </p>
              <p className="text-xs font-mono font-bold text-green-800">
                Bewerbung um einen Ausbildungsplatz als [Beruf] –
                Referenznummer: [XY] – [Dein Name]
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 rounded-xl px-4 py-3 text-xs">
          <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-1.5">
            Konkretes Beispiel:
          </p>
          <code className="text-amber-300 font-mono">
            Bewerbung als Kaufmann im Gesundheitswesen – Referenznummer:
            2024-HEK-001 – Ahmed Rahman
          </code>
        </div>
      </Accordion>

      {/* 04 — E-Mail Text & Anhang */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="📝"
        title="E-Mail-Bewerbung — Text & Anhang"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-3 mb-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-bold text-blue-800 mb-3">
              📧 Der E-Mail-Text
            </p>
            <div className="space-y-2">
              {[
                {
                  do: true,
                  text: "Schreibe kurz und höflich (Teaser) — verweise auf den Anhang",
                },
                {
                  do: true,
                  text: "Füge am Ende vollständige Kontaktdaten ein (Name, Telefon)",
                },
                {
                  do: false,
                  text: "Kopiere nicht dein ganzes Anschreiben in die E-Mail",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-sm text-blue-700"
                >
                  <span
                    className={`flex-shrink-0 mt-0.5 font-bold ${item.do ? "text-green-600" : "text-red-500"}`}
                  >
                    {item.do ? "✓" : "✗"}
                  </span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
            <p className="text-sm font-bold text-purple-800 mb-3">
              📎 Der Anhang — Nur eine Datei!
            </p>
            <div className="space-y-2">
              {[
                {
                  icon: "📦",
                  label: "Eine einzige PDF-Datei",
                  desc: "Alle Dokumente in einem PDF: Anschreiben + Lebenslauf + Zeugnisse",
                  color: "text-purple-700",
                },
                {
                  icon: "📏",
                  label: "Maximale Dateigröße",
                  desc: "Max. 2 MB — komprimiere deine PDFs wenn nötig",
                  color: "text-purple-700",
                },
                {
                  icon: "🏷️",
                  label: "Professioneller Dateiname",
                  desc: "Bewerbung_Ausbildung_Beruf_Vorname_Nachname.pdf",
                  color: "text-purple-700",
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-base flex-shrink-0">{item.icon}</span>
                  <div>
                    <p className={`text-sm font-bold ${item.color}`}>
                      {item.label}
                    </p>
                    <p className="text-xs text-purple-600 opacity-80">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <InfoBox
          variant="warning"
          icon="⚠️"
          title="Keine Bilder oder Links im E-Mail-Text"
        >
          Der Text der E-Mail soll nur als kurzer Teaser dienen — kein
          formatierter Text, keine eingebetteten Bilder. Alles Wichtige steht im
          Anhang.
        </InfoBox>
      </Accordion>

      {/* 05 — Beispiel E-Mail */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="💬"
        title="E-Mail-Bewerbung — Vollständiges Beispiel"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          So sieht eine professionelle Bewerbungs-E-Mail aus — kurz, höflich,
          klar strukturiert:
        </p>
        <EmailExample />
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            {
              icon: "✅",
              label: "Konkreter Ansprechpartner",
              color: "bg-green-50 border-green-200 text-green-700",
            },
            {
              icon: "✅",
              label: "Klare Betreffzeile mit Referenznummer",
              color: "bg-green-50 border-green-200 text-green-700",
            },
            {
              icon: "✅",
              label: "Kurzer Teaser — Anhang erwähnt",
              color: "bg-green-50 border-green-200 text-green-700",
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`rounded-lg border px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${item.color}`}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <InfoBox
          variant="tip"
          icon="🌍"
          title="Hinweis für Bewerber aus dem Ausland"
        >
          Weise in der E-Mail darauf hin, dass du für ein Online-Gespräch
          verfügbar bist — das zeigt Eigeninitiative und nimmt dem Recruiter die
          Unsicherheit über deinen Standort.
        </InfoBox>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Du weißt jetzt, wie du deine Bewerbung erfolgreich einreichst —
              per Portal oder per E-Mail.
            </p>
            <ul className="space-y-1">
              {[
                "Online-Portal: Account mit professioneller E-Mail erstellen",
                "Daten manuell eingeben — Lebenslauf als Vorlage bereithalten",
                "Immer ein zusammengeführtes PDF hochladen (Anschreiben + LL + Zeugnisse)",
                "E-Mail: Konkreten Ansprechpartner herausfinden, nicht 'Damen und Herren'",
                "Betreffzeile: Beruf – Referenznummer – Name",
                "E-Mail-Text kurz halten — Anhang verweisen, Kontaktdaten angeben",
                "Anhang: Eine PDF-Datei, max. 2 MB, professionell benannt",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="📋"
        title="Deine Aufgaben — Lektion 06"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Bewerbung ANFANGEN!</strong> — Wähle eine Stelle aus
                deiner Ordnerstruktur und reiche die erste Bewerbung ein
              </>,
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture06ErfolgreichBewerben;
